// src/components/auth/PublicAccessGuard.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useUser } from '../context/UserContext';
import { useAuth0User } from '../context/Auth0UserContext';

// Simple switch between Auth0 and original authentication
const useAuth0 = process.env.REACT_APP_USE_AUTH0 === 'true';

// Define which paths are allowed for unauthenticated users
const allowedPublicPaths = [
  /^\/$/,                              // Splash / Home
  /^\/explore$/,                       // Explore guest spots
  /^\/team$/,                          // Team page
  /^\/terms$/,                         // Terms of Service
  /^\/privacy$/,                       // Privacy Policy
  /^\/login$/,                         // Login page
  /^\/signup$/,                        // Signup page (original auth mode)
  /^\/auth0-callback$/,                // Auth0 redirect/callback
  /^\/invite-signup$/,                 // Invite-only signup for creators
  /^\/continue-request$/,          // Continue request auth flow (for guests)
  /^\/creator\/[^/]+\/[^/]+$/,         // Public shoot page (with inline request flow)
  /^\/creator\/[^/]+\/[^/]+\/[^/]+$/,  // Public guest spot details page (/creator/:username/:shootSlug/:spotSlug)
  /^\/creator\/[^/]+\/[^/]+\/[^/]+\/request$/,  // Request guest spot (new format: /creator/:username/:shootSlug/:spotSlug/request)
  /^\/[^/]+\/[^/]+$/,                  // Shorter public shoot page (e.g. /kaicenat/24-hour-gaming-marathon)
  /^\/guest-spot\/\d+$/,               // Guest spot details (read-only)
  /^\/guest-spot\/\d+\/request$/,        // Request guest spot (old format - backwards compatibility)
  /^\/shoots\/\d+$/,                   // Shoot details by ID (read-only)
  /^\/guest-shoot\/\d+$/,              // Legacy/alternate shoot details route
  /^\/host\/[^/]+$/,                   // Host profile page (read-only)
];

const isAllowedForGuest = (path: string) => {
  return allowedPublicPaths.some((rx) => rx.test(path));
};

interface Props {
  children: React.ReactNode;
}

// Auth0 Public Access Guard
function Auth0PublicAccessGuard({ children }: Props) {
  const { user, loading } = useAuth0User();
  return <PublicAccessGuardComponent user={user} loading={loading} children={children} />;
}

// Original Public Access Guard
function OriginalPublicAccessGuard({ children }: Props) {
  const { user, loading } = useUser();
  return <PublicAccessGuardComponent user={user} loading={loading} children={children} />;
}

// Main Public Access Guard - Renders appropriate authentication wrapper
const PublicAccessGuard: React.FC<Props> = ({ children }) => {
  return useAuth0 ? <Auth0PublicAccessGuard children={children} /> : <OriginalPublicAccessGuard children={children} />;
};

// Shared Public Access Guard Component
function PublicAccessGuardComponent({ user, loading, children }: { user: any, loading: boolean, children: React.ReactNode }) {
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#2D2070' }} />
      </Box>
    );
  }

  // If logged in, allow access to all routes
  // If not logged in, allow public pages - unknown routes will fall through to 404
  // We only redirect to home for PROTECTED routes that require auth
  // Let the 404 route handle truly unknown paths
  
  return <>{children}</>;
}

export default PublicAccessGuard;
